/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState, useRef, useEffect} from 'react'
import parse from 'html-react-parser'
import {useIntl} from 'react-intl'
import PropTypes from 'prop-types'
import {Box, Heading, Button, Divider} from '@salesforce/retail-react-app/app/components/shared/ui'

const ProductDescription = ({product}) => {
    const {formatMessage} = useIntl()
    const [isExpanded, setIsExpanded] = useState(false)
    const [showReadMore, setShowReadMore] = useState(false)
    const contentRef = useRef(null)

    const shippingRestrictionsValues = product?.c_productShippingRestriction?.filter(
        (restriction) => restriction.includes('US')
    )

    useEffect(() => {
        if (contentRef.current) {
            const scrollHeight = contentRef.current.scrollHeight
            setShowReadMore(scrollHeight > 150)
        }
        // Target links inside that dynamic description
        const links = contentRef.current.querySelectorAll('a, button')
        links.forEach((link) => {
            link.setAttribute('tabindex', '-1')
        })
    }, [product])

    const toggleExpanded = () => {
        setIsExpanded(!isExpanded)
        if (contentRef.current) {
            const links = contentRef.current.querySelectorAll('a, button')
            links.forEach((link) => {
                if (isExpanded) {
                    link.setAttribute('tabindex', '-1')
                } else {
                    link.removeAttribute('tabindex')
                }
            })
        }
    }

    // Transform HTML attributes to React-compatible format
    const parseOptions = {
        replace: (domNode) => {
            if (domNode.type === 'tag' && domNode.attribs) {
                const newAttribs = {}
                Object.keys(domNode.attribs).forEach((attr) => {
                    // Convert common HTML event handlers to React format
                    switch (attr.toLowerCase()) {
                        case 'onfocus':
                            newAttribs.onFocus = domNode.attribs[attr]
                            break
                        case 'onblur':
                            newAttribs.onBlur = domNode.attribs[attr]
                            break
                        case 'onclick':
                            newAttribs.onClick = domNode.attribs[attr]
                            break
                        case 'onchange':
                            newAttribs.onChange = domNode.attribs[attr]
                            break
                        case 'onmouseover':
                            newAttribs.onMouseOver = domNode.attribs[attr]
                            break
                        case 'onmouseout':
                            newAttribs.onMouseOut = domNode.attribs[attr]
                            break
                        default:
                            newAttribs[attr] = domNode.attribs[attr]
                    }
                })
                domNode.attribs = newAttribs
            }
        }
    }

    return (
        <Box pos={'relative'}>
            <Divider
                borderWidth="1px"
                borderColor="black"
                borderStyle="solid"
                opacity={'1'}
                mb={{base: '6', xl: '8'}}
                mt={{base: '4', md: '2'}}
                display={{base: 'none', sm: 'inherit'}}
            />
            {/* Heading */}
            <Heading
                fontSize={{base: 'fontSize32', md: 'fontSize28', lg: 'fontSize32'}}
                mb={6}
                lineHeight={{base: '10', md: '9', lg: '10'}}
                fontWeight={'extrabold'}
            >
                {formatMessage({
                    defaultMessage: 'Description',
                    id: 'product_description.heading.product_description'
                })}
            </Heading>

            {/* Content Container */}
            <Box
                ref={contentRef}
                maxHeight={isExpanded ? 'none' : '192px'}
                overflow="hidden"
                position="relative"
            >
                {/* Short Description */}
                {product?.shortDescription && (
                    <Box
                        sx={{
                            p: {
                                marginBottom: '16px'
                            },
                            ul: {
                                margin: '25px'
                            }
                        }}
                    >
                        {parse(product.shortDescription, parseOptions)}
                    </Box>
                )}

                {/* Shipping Restriction */}
                {product?.c_productShippingRestriction && shippingRestrictionsValues && (
                    <Box color="red" mb={'14px'}>
                        {formatMessage(
                            {
                                defaultMessage:
                                    'Due to licensing and/or shipping restrictions, this product is only available for shipping within the {shippingRestrictions}',
                                id: 'product_shippingRestrictions'
                            },
                            {
                                shippingRestrictions: shippingRestrictionsValues.join(', ')
                            }
                        )}
                    </Box>
                )}
            </Box>

            {/* Read More/Less Button */}
            {showReadMore && (
                <Box
                    width={!isExpanded ? '100%' : 'auto'}
                    pt={!isExpanded ? '64px' : 0}
                    pb={!isExpanded ? '8px' : 0}
                    px={!isExpanded ? 0 : undefined}
                    position={!isExpanded ? 'absolute' : 'relative'}
                    bottom={!isExpanded ? 0 : undefined}
                    justifyContent={!isExpanded ? 'left' : undefined}
                    background={
                        !isExpanded
                            ? 'linear-gradient(180deg, rgba(248, 246, 243, 0.00) 0%, rgba(248, 246, 243, 0.95) 47.6%, #F8F6F3 100%)'
                            : undefined
                    }
                >
                    <Button
                        variant="link"
                        color="woodsmoke"
                        textDecoration="underline"
                        textUnderlineOffset="2px"
                        fontWeight="500"
                        p={0}
                        h="auto"
                        mt={isExpanded ? 2 : 0}
                        lineHeight={'6'}
                        onClick={toggleExpanded}
                        _focusVisible={{
                            borderRadius: '0'
                        }}
                        aria-expanded={isExpanded}
                    >
                        {isExpanded ? 'Read Less' : 'Read More'}
                    </Button>
                </Box>
            )}
        </Box>
    )
}

ProductDescription.propTypes = {
    product: PropTypes.object
}

export default ProductDescription
